import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import client from '../shopify/client';
import { GET_PRODUCT_BY_HANDLE } from '../shopify/queries';
import { useCart } from '../context/CartContext';
import './Product.css';

export default function Product() {
    const { handle } = useParams();
    const { addToCart, loading: cartLoading } = useCart();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        setLoading(true);
        client
            .request(GET_PRODUCT_BY_HANDLE, { variables: { handle } })
            .then(({ data }) => {
                const p = data.product;
                if (!p) {
                    setError('Produkt nicht gefunden.');
                    return;
                }
                setProduct(p);
                setSelectedVariant(p.variants.edges[0]?.node ?? null);
            })
            .catch(() => setError('Produkt konnte nicht geladen werden.'))
            .finally(() => setLoading(false));
    }, [handle]);

    if (loading) {
        return (
            <div className="shop-loading">
                <span className="shop-spinner" />
            </div>
        );
    }

    if (error) return <p className="shop-error">{error}</p>;
    if (!product) return null;

    const images = product.images.edges.map((e) => e.node);
    const variants = product.variants.edges.map((e) => e.node);
    const price = parseFloat(selectedVariant?.price.amount ?? product.priceRange.minVariantPrice.amount);
    const currency = selectedVariant?.price.currencyCode ?? product.priceRange.minVariantPrice.currencyCode;

    const formatPrice = (amount, cur) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format(amount);

    const handleAddToCart = async () => {
        if (!selectedVariant?.availableForSale) return;
        await addToCart(selectedVariant.id, 1);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    };

    // Group options by name for variant selector
    const optionNames = [...new Set(variants.flatMap((v) => v.selectedOptions.map((o) => o.name)))];

    return (
        <main className="product-page">
            {/* Image gallery */}
            <div className="product-gallery">
                <div className="product-gallery-main">
                    {images[activeImage] ? (
                        <img src={images[activeImage].url} alt={images[activeImage].altText || product.title} className="product-main-img" />
                    ) : (
                        <div className="product-no-img" />
                    )}
                </div>
                {images.length > 1 && (
                    <div className="product-thumbnails">
                        {images.map((img, i) => (
                            <button key={i} className={`product-thumb${i === activeImage ? ' active' : ''}`} onClick={() => setActiveImage(i)}>
                                <img src={img.url} alt={img.altText || `Bild ${i + 1}`} />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Product info */}
            <div className="product-info">
                <Link to="/shop" className="product-back">
                    ← Zurück zum Shop
                </Link>
                <h1 className="product-title">{product.title}</h1>
                <p className="product-price">{formatPrice(price, currency)}</p>

                {/* Variant selectors */}
                {optionNames.map((optName) => {
                    const values = [...new Set(variants.flatMap((v) => v.selectedOptions.filter((o) => o.name === optName).map((o) => o.value)))];
                    return (
                        <div key={optName} className="product-option">
                            <p className="product-option-name">{optName}</p>
                            <div className="product-option-values">
                                {values.map((val) => {
                                    const matchVariant = variants.find((v) => v.selectedOptions.some((o) => o.name === optName && o.value === val));
                                    const isSelected = selectedVariant?.selectedOptions.some((o) => o.name === optName && o.value === val);
                                    return (
                                        <button
                                            key={val}
                                            className={`option-btn${isSelected ? ' selected' : ''}${!matchVariant?.availableForSale ? ' disabled' : ''}`}
                                            onClick={() => matchVariant && setSelectedVariant(matchVariant)}
                                            disabled={!matchVariant?.availableForSale}>
                                            {val}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                <button
                    className={`add-to-cart-btn${!selectedVariant?.availableForSale ? ' sold-out' : ''}${added ? ' added' : ''}`}
                    onClick={handleAddToCart}
                    disabled={!selectedVariant?.availableForSale || cartLoading}>
                    {!selectedVariant?.availableForSale ? 'Ausverkauft' : added ? 'Hinzugefügt ✓' : 'In den Warenkorb'}
                </button>

                {product.description && (
                    <div className="product-description">
                        <p>{product.description}</p>
                    </div>
                )}
            </div>
        </main>
    );
}
